import { Download, FileText, Trash2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { downloadFile, useUploadFile } from '@/api/files'
import { errorMessage } from '@/api/errors'
import { EmptyState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Button, IconButton } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Can } from '@/features/auth/guards'
import { intlLocale } from '@/i18n'
import { formatDate } from '@/utils/format'
import { useStudentDocuments } from '../api'
import type { StoredFile } from '../types'

/** File size split into a localised number and its unit ('MB' / 'KB'), translated at render. */
function size(bytes: number): { value: string; unit: 'MB' | 'KB' } {
  if (bytes > 1024 * 1024) {
    const fmt = new Intl.NumberFormat(intlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    return { value: fmt.format(bytes / 1024 / 1024), unit: 'MB' }
  }
  return { value: String(Math.max(1, Math.round(bytes / 1024))), unit: 'KB' }
}

/** Private documents (birth certificate, previous reports…) stored in MinIO, streamed back through the API. */
export function DocumentsPanel({ studentId, documents }: { studentId: number; documents: StoredFile[] }) {
  const { t } = useTranslation('students')
  const input = useRef<HTMLInputElement>(null)
  const upload = useUploadFile()
  const { attach, remove } = useStudentDocuments(studentId)
  const [pendingDelete, setPendingDelete] = useState<StoredFile | null>(null)
  const formatSize = (bytes: number) => {
    const { value, unit } = size(bytes)
    return unit === 'MB' ? t('{{size}} MB', { size: value }) : t('{{size}} KB', { size: value })
  }

  const onFile = async (file: File | undefined) => {
    if (!file) return
    try {
      const stored = await upload.mutateAsync({ file, category: 'document' })
      await attach.mutateAsync(stored.id)
      toast.success(t('Document uploaded'), file.name)
    } catch (e) {
      toast.error(t('Upload failed'), errorMessage(e))
    } finally {
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div>
      <Can permission="students.manage">
        <div className="mb-4 flex items-center justify-between gap-3">
          <p className="text-sm text-muted">
            {t('PDF, Word or image files up to 10 MB. Documents are private.')}
          </p>
          <input
            ref={input}
            type="file"
            className="sr-only"
            aria-label={t('Upload document')}
            accept=".pdf,.doc,.docx,image/*"
            onChange={(e) => void onFile(e.target.files?.[0])}
          />
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Upload />}
            loading={upload.isPending || attach.isPending}
            onClick={() => input.current?.click()}
          >
            {t('Upload')}
          </Button>
        </div>
      </Can>
      {documents.length === 0 ? (
        <EmptyState
          icon={<FileText />}
          title={t('No documents')}
          description={t('Upload admission paperwork, certificates or reports.')}
          compact
        />
      ) : (
        <ul className="divide-y divide-line rounded-control border border-line">
          {documents.map((d) => (
            <li key={d.id} className="flex items-center gap-3 px-4 py-3">
              <span className="grid size-9 place-items-center rounded-control bg-brand-50 text-brand-600">
                <FileText className="size-4" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">{d.originalName}</p>
                <p className="text-xs text-muted">
                  {formatSize(d.size)} · {formatDate(d.createdAt)}
                </p>
              </div>
              <IconButton
                label={t('Download {{name}}', { name: d.originalName })}
                icon={<Download />}
                onClick={() =>
                  void downloadFile(d.downloadUrl, d.originalName).catch((e: unknown) =>
                    toast.error(t('Download failed'), errorMessage(e)),
                  )
                }
              />
              <Can permission="students.manage">
                <IconButton
                  label={t('Delete {{name}}', { name: d.originalName })}
                  icon={<Trash2 />}
                  onClick={() => setPendingDelete(d)}
                />
              </Can>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => (o ? undefined : setPendingDelete(null))}
        title={t('Delete this document?')}
        description={
          pendingDelete
            ? t('“{{name}}” will be permanently removed from storage.', { name: pendingDelete.originalName })
            : undefined
        }
        confirmLabel={t('Delete')}
        loading={remove.isPending}
        onConfirm={() => {
          if (!pendingDelete) return
          remove.mutate(pendingDelete.id, {
            onSuccess: () => {
              toast.success(t('Document deleted'))
              setPendingDelete(null)
            },
            onError: (e) => toast.error(t('Could not delete'), errorMessage(e)),
          })
        }}
      />
    </div>
  )
}
